import React from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Users, Rocket, Heart, Lightbulb, ArrowRight, Star, Coffee, Zap, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const TiltCard = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`relative ${className}`}
    >
      {children}
    </motion.div>
  );
};

const JoinUs = () => {
  const stats = [
    { label: 'Team Members', value: '50+', icon: Users },
    { label: 'Projects Done', value: '200+', icon: Zap },
    { label: 'Happy Clients', value: '150+', icon: Heart },
    { label: 'Tech Stack', value: '20+', icon: Lightbulb },
  ];

  const galleryImages = [
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=800"
  ];

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen transition-colors duration-500 overflow-x-hidden">
      {/* Hero Section with 3D Parallax Effect */}
      <section className="bg-[#002a5c] py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 text-center lg:text-left"
            >
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full mb-6 text-sm font-semibold border border-blue-500/30"
              >
                <Rocket size={16} /> <span>Join Our Elite Team</span>
              </motion.div>
              <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tight leading-tight">
                Architecting the <span className="text-blue-400">Digital Frontier</span>
              </h1>
              <p className="text-blue-100/80 text-lg md:text-xl max-w-2xl mx-auto lg:mx-0 leading-relaxed mb-12">
                OETechs isn't just a company; it's a collective of visionaries, engineers, and designers crafting the next generation of digital experiences.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
                <Link to="/careers" className="group w-full sm:w-auto px-10 py-5 bg-[#4F46E5] text-[#fff] rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-2xl shadow-yellow-500/20 flex items-center justify-center gap-2">
                  Launch Your Career <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="#culture" className="w-full sm:w-auto px-10 py-5 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all border border-white/10 backdrop-blur-md">
                  Explore Our DNA
                </a>
              </div>
            </motion.div>

            <div className="flex-1 relative">
              <TiltCard className="w-full aspect-square max-w-md mx-auto">
                <div className="relative w-full h-full">
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-[3rem] rotate-6 opacity-20 animate-pulse" />
                  <div className="absolute inset-0 bg-gradient-to-bl from-purple-600 to-pink-600 rounded-[3rem] -rotate-6 opacity-20 animate-pulse delay-700" />
                  <div className="relative w-full h-full bg-white/5 backdrop-blur-xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000" 
                      className="w-full h-full object-cover opacity-80 mix-blend-overlay"
                      alt="Team Collaboration"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4F46E5] via-transparent to-transparent" />
                    <div className="absolute bottom-8 left-8 right-8">
                      <div className="flex gap-2 mb-4">
                        {[1, 2, 3, 4, 5].map(i => <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />)}
                      </div>
                      <p className="text-white font-semibold text-xl mb-2">"Best place to innovate and grow together."</p>
                      <p className="text-blue-300 text-sm">- Technical Lead, OETechs</p>
                    </div>
                  </div>
                </div>
              </TiltCard>
            </div>
          </div>
        </div>
        
        {/* Animated Background Orbs */}
        <motion.div 
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] -mr-64 -mt-64" 
        />
        <motion.div 
          animate={{ scale: [1.2, 1, 1.2], rotate: [0, -90, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
          className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] -ml-64 -mb-64" 
        />
      </section>

      {/* Stats Counter Section */}
      <section className="py-20 bg-white dark:bg-[#001a35] relative z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 hover:shadow-xl transition-all group"
              >
                <div className="w-12 h-12 bg-blue-600/10 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <stat.icon size={24} />
                </div>
                <h3 className="text-3xl font-black text-[#4F46E5] dark:text-white mb-1">{stat.value}</h3>
                <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section with 3D Cards */}
      <section className="py-32" id="culture">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <motion.span 
              whileInView={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              className="text-blue-600 font-black uppercase tracking-[0.3em] text-xs mb-4 block"
            >
              Our Core Values
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-black text-[#4F46E5] dark:text-white mb-6">Why Join OETechs?</h2>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto">
              We've built an environment where creativity meets logic, and every individual has the power to shape the future.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { icon: Rocket, title: 'Rapid Growth', desc: 'Accelerate your career with structured mentorship and high-impact projects.', color: 'bg-blue-500' },
              { icon: Coffee, title: 'Work-Life Flow', desc: 'We value your results, not your seat time. Flexible hybrid culture.', color: 'bg-pink-500' },
              { icon: Lightbulb, title: 'Innovation Labs', desc: 'Got a crazy idea? Our internal R&D labs are where you bring it to life.', color: 'bg-amber-500' },
              { icon: ShieldCheck, title: 'Stability & Trust', desc: 'Join a globally recognized firm with a strong foundation and long-term vision.', color: 'bg-emerald-500' },
              { icon: Zap, title: 'Cutting Edge Tech', desc: 'Work with the latest frameworks, AI tools, and architectural patterns.', color: 'bg-purple-500' },
              { icon: Heart, title: 'Vibrant Community', desc: 'Regular team retreats, hackathons, and a culture of celebration.', color: 'bg-indigo-500' }
            ].map((item, i) => (
              <TiltCard key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="h-full bg-white dark:bg-[#001a35] p-10 rounded-[3rem] shadow-sm border border-slate-100 dark:border-white/10 hover:shadow-2xl transition-all group flex flex-col items-center text-center"
                >
                  <div className={`w-20 h-20 rounded-3xl ${item.color} text-white flex items-center justify-center mb-8 shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <item.icon size={36} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#4F46E5] dark:text-white mb-4">{item.title}</h3>
                  <p className="text-slate-500 dark:text-slate-400 leading-relaxed text-sm">
                    {item.desc}
                  </p>
                </motion.div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Image Gallery / Office Section */}
      <section className="py-32 bg-[#002a5c] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-20">
            <div className="flex-1 space-y-8">
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">Our Workspace <br/><span className="text-blue-400">Our Playground</span></h2>
              <p className="text-blue-200 text-lg leading-relaxed">
                Experience a space designed for collaboration, deep focus, and technical excellence. Our offices in Hyderabad are hubs of digital innovation.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {galleryImages.map((img, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05, rotate: i % 2 === 0 ? 2 : -2 }}
                    className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 aspect-[4/3]"
                  >
                    <img src={img} className="w-full h-full object-cover" alt={`Office ${i}`} />
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="flex-1 lg:pl-10">
              <div className="bg-white/5 backdrop-blur-xl p-12 rounded-[4rem] border border-white/10 relative">
                <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#4F46E5] rounded-2xl flex items-center justify-center shadow-lg -rotate-12">
                  <Zap size={32} className="text-[#4F46E5]" />
                </div>
                <h3 className="text-3xl font-black text-white mb-8">Ready to Scale?</h3>
                <div className="space-y-6 mb-12">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-1">
                      <ArrowRight size={14} className="text-white" />
                    </div>
                    <p className="text-blue-100 font-medium">Global Exposure with Fortune 500 Clients</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-1">
                      <ArrowRight size={14} className="text-white" />
                    </div>
                    <p className="text-blue-100 font-medium">AI-Integrated Workflows & Learning</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-1">
                      <ArrowRight size={14} className="text-white" />
                    </div>
                    <p className="text-blue-100 font-medium">Industry-Leading Compensation Packages</p>
                  </div>
                </div>
                <Link to="/careers" className="w-full py-5 bg-white text-[#4F46E5] rounded-2xl font-black text-center block hover:bg-blue-50 transition-colors shadow-2xl">
                  Check Open Positions
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:40px_40px]" />
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-blue-600 blur-[50px] opacity-20 animate-pulse" />
            <div className="relative w-24 h-24 bg-white dark:bg-[#001a35] rounded-[2.5rem] shadow-xl flex items-center justify-center mx-auto border border-slate-100 dark:border-white/10">
              <Users size={40} className="text-blue-600" />
            </div>
          </div>
          <h2 className="text-4xl font-black text-[#4F46E5] dark:text-white mb-6">Don't Just Find a Job. <br/>Find Your Purpose.</h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg mb-12 max-w-2xl mx-auto">
            Whether you're a seasoned pro or a rising star, we'd love to hear your story. Let's create something remarkable together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to="/careers" className="w-full sm:w-auto px-12 py-5 bg-blue-600 text-white rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-xl shadow-blue-500/20">
              Apply Now
            </Link>
            <Link to="/contact" className="w-full sm:w-auto px-12 py-5 bg-white dark:bg-white/5 text-[#4F46E5] dark:text-white rounded-2xl font-semibold hover:bg-slate-50 transition-all border border-slate-200 dark:border-white/10">
              Talk to HR
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinUs;
